import React, { useState } from 'react';
import PropTypes from 'prop-types';
import EmailIcon from '@tradera/blueprint/theme/icons2/network-email.svg?jsx';
import FacebookIcon from '@tradera/blueprint/theme/icons2/network-fb.svg?jsx';
import PinterestIcon from '@tradera/blueprint/theme/icons2/network-pin.svg?jsx';
import { useTranslator } from 'tradera/lang/translate';
import CopyToClipboardUtil from 'utils/copy-to-clipboard';
import {
    getEmailShareLink,
    getFacebookShareLink,
    getPinterestShareLink
} from 'tradera/utils/share-link';
import { useLocation } from 'tradera/hooks/use-location';
import { Button } from '../atoms/button/button';
import { HeaderWithCloseButton } from '../scrollable-modal/header-with-close-button';
import { ScrollableModal } from '../scrollable-modal/scrollable-modal';

const ShareModal = ({
    onDismiss,
    shareTitle,
    shareImageUrl,
    translationKeys,
    gaCategory,
    shareLinkHref: givenHref
}) => {
    const { t } = useTranslator();
    const [showCopiedNotification, setShowCopiedNotification] = useState(false);
    const location = useLocation();
    const shareLinkHref = givenHref || location.href;
    const copyLink = () => {
        CopyToClipboardUtil.copy(linkRef);
        setShowCopiedNotification(true);
        setTimeout(() => setShowCopiedNotification(false), 1000);
    };

    let linkRef = null;

    const setLinkRef = (element) => {
        if (element && !linkRef) {
            // eslint-disable-next-line better-mutation/no-mutation
            linkRef = element;
        }
    };

    const {
        shareHeader,
        shareMailSubject,
        shareMailBody,
        sharePinterestDescription,
        shareLinkText,
        shareCopy = 'shareModal_copy',
        shareCopied = 'shareModal_copied',
        shareClose = 'shareModal_close'
    } = translationKeys;

    return (
        <ScrollableModal
            show
            maxWidth="30rem"
            onClose={onDismiss}
            header={<HeaderWithCloseButton onClose={onDismiss} />}
            footer={
                <Button theme="primary" fluid onClick={onDismiss}>
                    {t(shareClose)}
                </Button>
            }>
            <h2 className="heading-berlin text-center text-styled">
                {t(shareHeader)}
            </h2>
            <div className="d-flex justify-content-between p-5 border-bottom">
                <a
                    href={getEmailShareLink({
                        subject: t(shareMailSubject, {
                            title: shareTitle
                        }),
                        body: t(shareMailBody, {
                            url: shareLinkHref
                        })
                    })}
                    rel="nofollow"
                    data-ga-category={gaCategory}
                    data-ga-action="Navigation"
                    data-ga-label="Sociala medier - Mail">
                    <EmailIcon className="icon icon-xl icon-dark" />
                </a>
                <a
                    href={getFacebookShareLink({
                        url: shareLinkHref
                    })}
                    rel="nofollow"
                    data-ga-category={gaCategory}
                    data-ga-action="Navigation"
                    data-ga-label="Sociala medier - Facebook">
                    <FacebookIcon className="icon icon-xl icon-dark" />
                </a>
                <a
                    href={getPinterestShareLink({
                        url: shareLinkHref,
                        description: t(sharePinterestDescription, {
                            title: shareTitle
                        }),
                        imageUrl: shareImageUrl
                    })}
                    rel="nofollow"
                    data-ga-category={gaCategory}
                    data-ga-action="Navigation"
                    data-ga-label="Sociala medier - Pinterest">
                    <PinterestIcon className="icon icon-xl icon-dark" />
                </a>
            </div>
            <div className="row align-items-center py-4 border-bottom mx-0">
                <div className="col-12 col-md-9">
                    <p className="text-inter-heavy mb-1">{t(shareLinkText)}</p>
                    <p className="text-break" ref={setLinkRef}>
                        {shareLinkHref}
                    </p>
                </div>
                <div className="col-12 col-md-3">
                    <Button
                        theme="primary"
                        variant="outline"
                        onClick={copyLink}>
                        {t(shareCopy)}
                    </Button>
                    <div
                        className={`position-fixed bg-dark text-white p-1 mt-1 ${
                            showCopiedNotification ? 'd-block' : 'd-none'
                        }`}>
                        {t(shareCopied)}
                    </div>
                </div>
            </div>
        </ScrollableModal>
    );
};

ShareModal.propTypes = {
    onDismiss: PropTypes.func.isRequired,
    shareTitle: PropTypes.string.isRequired,
    shareImageUrl: PropTypes.string.isRequired,
    translationKeys: PropTypes.shape({
        shareHeader: PropTypes.string.isRequired,
        shareMailSubject: PropTypes.string.isRequired,
        shareMailBody: PropTypes.string.isRequired,
        sharePinterestDescription: PropTypes.string.isRequired,
        shareLinkText: PropTypes.string.isRequired,
        shareCopy: PropTypes.string,
        shareCopied: PropTypes.string,
        shareClose: PropTypes.string
    }),
    shareLinkHref: PropTypes.string
};

export { ShareModal };
